const PrivacyPage = () => {
  return (
    <div className="pt-20" data-testid="privacy-page">
      {/* Hero Section */}
      <section className="bg-forest-950 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-emerald-100/80 text-lg">
              Last updated: December 2024
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <div className="space-y-8 text-gray-700">
              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">Introduction</h2>
                <p>
                  Nexivas Lawn Care ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
                </p>
                <p className="mt-4">
                  By using our website or services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">Information We Collect</h2>
                <h3 className="text-xl font-heading font-semibold text-forest-950 mb-3">Personal Information</h3>
                <p>We may collect personal information that you voluntarily provide to us when you:</p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Fill out a contact or quote request form</li>
                  <li>Subscribe to our newsletter</li>
                  <li>Request information about our services</li>
                  <li>Schedule a service appointment</li>
                </ul>
                <p className="mt-4">This information may include:</p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Mailing/service address</li>
                  <li>Property information</li>
                  <li>Service preferences</li>
                </ul>

                <h3 className="text-xl font-heading font-semibold text-forest-950 mb-3 mt-6">Automatically Collected Information</h3>
                <p>When you visit our website, we may automatically collect certain information, including:</p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Device type</li>
                  <li>Operating system</li>
                  <li>Pages visited and time spent</li>
                  <li>Referring website</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">How We Use Your Information</h2>
                <p>We use the information we collect for the following purposes:</p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>To provide and maintain our services</li>
                  <li>To respond to your inquiries and requests</li>
                  <li>To process and schedule service appointments</li>
                  <li>To send you quotes and service-related communications</li>
                  <li>To improve our website and services</li>
                  <li>To send marketing communications (with your consent)</li>
                  <li>To comply with legal obligations</li>
                  <li>To detect and prevent fraud</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">Cookies and Tracking Technologies</h2>
                <p>
                  We use cookies and similar tracking technologies to track activity on our website and store certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier.
                </p>
                <h3 className="text-xl font-heading font-semibold text-forest-950 mb-3 mt-4">Types of Cookies We Use:</h3>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li><strong>Essential Cookies:</strong> Required for website functionality</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website</li>
                  <li><strong>Marketing Cookies:</strong> Used to track visitors across websites for advertising purposes</li>
                </ul>
                <p className="mt-4">
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">Google Ads and Analytics</h2>
                <p>
                  We use Google Analytics and Google Ads to analyze website traffic and optimize our advertising campaigns. These services may collect information about your online activities over time and across different websites.
                </p>
                <p className="mt-4">
                  For more information on Google's privacy practices, please visit: <a href="https://policies.google.com/privacy" className="text-emerald-600 hover:text-emerald-700" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>
                </p>
                <p className="mt-4">
                  You can opt out of Google Analytics by installing the Google Analytics Opt-out Browser Add-on.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">Data Protection</h2>
                <p>
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>SSL encryption for data transmission</li>
                  <li>Secure server infrastructure</li>
                  <li>Limited access to personal information</li>
                  <li>Regular security assessments</li>
                </ul>
                <p className="mt-4">
                  However, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security of your data.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">Sharing Your Information</h2>
                <p>We may share your information in the following situations:</p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li><strong>Service Providers:</strong> With third-party service providers who perform services on our behalf</li>
                  <li><strong>Legal Requirements:</strong> If required by law or in response to valid legal requests</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                  <li><strong>With Your Consent:</strong> For any other purpose with your explicit consent</li>
                </ul>
                <p className="mt-4">
                  We do not sell, trade, or rent your personal information to third parties for their marketing purposes.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">Your Rights</h2>
                <p>You have the right to:</p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Opt out of marketing communications</li>
                  <li>Withdraw consent where we rely on consent for processing</li>
                </ul>
                <p className="mt-4">
                  To exercise any of these rights, please contact us using the information provided below.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">Children's Privacy</h2>
                <p>
                  Our services are not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-forest-950 mb-4">Contact Us</h2>
                <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
                <div className="mt-4 bg-emerald-50 rounded-xl p-6">
                  <p><strong>Nexivas Lawn Care</strong></p>
                  <p>667 Magnolia Court</p>
                  <p>Houston, TX 57002</p>
                  <p className="mt-3">
                    <strong>Email:</strong> <a href="mailto:support@nexivas.store" className="text-emerald-600 hover:text-emerald-700">support@nexivas.store</a>
                  </p>
                  <p>
                    <strong>Phone:</strong> <a href="tel:+1-771-1871-987" className="text-emerald-600 hover:text-emerald-700">+1-771-1871-987</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
